"use client"

import { Card, CardContent } from "@/components/ui/card"
import { FileText, Shield, Scale, AlertTriangle, Zap, Globe, FileCode, MessageSquare } from "lucide-react"
import { useEffect } from "react"

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    icon: FileText,
    content: `By accessing or using Roomvia's services, website, or applications (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.

These Terms constitute a legally binding agreement between you and Roomvia, Inc. ("Roomvia," "we," "us," or "our") regarding your use of the Service. Please read them carefully.

We may modify these Terms at any time. If we do so, we will notify you by posting the modified Terms on the website or through the Service. Your continued use of the Service after any such changes constitutes your acceptance of the modified Terms.`,
  },
  {
    id: "account",
    title: "Account Registration and Security",
    icon: Shield,
    content: `To use certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.

You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.

We reserve the right to disable any user account at any time if, in our opinion, you have failed to comply with these Terms or if we suspect fraudulent or abusive activity.`,
  },
  {
    id: "service-description",
    title: "Service Description",
    icon: Zap,
    content: `Roomvia provides an AI-powered interior design platform that allows users to generate design concepts, visualize spaces, and access design recommendations.

We may update, modify, or discontinue any aspect of the Service at any time without prior notice. We are not liable to you or any third party for any modification, suspension, or discontinuance of the Service.

The Service may include features that operate on a credit-based system. Credits may be purchased or provided as part of a subscription plan. Credits have no monetary value, cannot be transferred, and expire as specified in your plan details.`,
  },
  {
    id: "user-content",
    title: "User Content and License",
    icon: FileCode,
    content: `"User Content" refers to any information, data, or material that you upload, submit, or otherwise make available through the Service, including room photos, design preferences, and project details.

You retain all rights in your User Content. By submitting User Content, you grant Roomvia a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute your User Content for the purposes of operating and improving the Service.

You represent and warrant that: (i) you own or have the necessary rights to your User Content; (ii) your User Content does not infringe the intellectual property rights or other rights of any third party; and (iii) your User Content complies with these Terms and all applicable laws.

We may use anonymized and aggregated User Content to train and improve our AI models. If you do not want your content used for this purpose, please contact us at support@roomvia.com.`,
  },
  {
    id: "prohibited-conduct",
    title: "Prohibited Conduct",
    icon: AlertTriangle,
    content: `You agree not to:

- Use the Service for any illegal purpose or in violation of any laws
- Impersonate any person or entity or falsely state your affiliation
- Interfere with or disrupt the Service or servers connected to the Service
- Attempt to gain unauthorized access to any part of the Service
- Use the Service to transmit any viruses, worms, or malicious code
- Scrape, data-mine, or extract data from the Service using automated means
- Use the Service to generate content that infringes on third-party rights
- Resell, duplicate, or reproduce the designs or other outputs from the Service without authorization
- Use the Service to generate content that is offensive, harmful, or violates our content policies

We reserve the right to remove any User Content and suspend or terminate your account for violations of these prohibitions.`,
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property Rights",
    icon: Scale,
    content: `The Service and its original content, features, and functionality are owned by Roomvia and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.

The Roomvia name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Roomvia or its affiliates. You may not use such marks without our prior written permission.

Design outputs generated by the Service are licensed to you for personal or commercial use as specified in your subscription plan. However, Roomvia retains ownership of the underlying AI models, algorithms, and technology used to generate these designs.`,
  },
  {
    id: "payment",
    title: "Payment Terms",
    icon: FileText,
    content: `If you choose a paid subscription or purchase credits, you agree to pay all fees in accordance with the applicable pricing and payment terms. All payments are non-refundable except as expressly set forth in these Terms or as required by applicable law.

We use third-party payment processors and do not store your complete payment information. By providing payment information, you represent that you are authorized to use the payment method.

Subscription plans automatically renew unless canceled before the renewal date. You can cancel your subscription at any time through your account settings or by contacting customer support.

We reserve the right to change our prices upon notice. Price changes will apply to billing cycles after the notice.`,
  },
  {
    id: "disclaimer",
    title: "Disclaimer of Warranties",
    icon: AlertTriangle,
    content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.

ROOMVIA DOES NOT WARRANT THAT: (A) THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; (B) ANY ERRORS OR DEFECTS WILL BE CORRECTED; (C) THE SERVICE IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; OR (D) THE RESULTS OF USING THE SERVICE WILL MEET YOUR REQUIREMENTS.

DESIGN RECOMMENDATIONS AND VISUALIZATIONS PROVIDED BY THE SERVICE ARE FOR INFORMATIONAL PURPOSES ONLY. ROOMVIA DOES NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR FEASIBILITY OF ANY DESIGNS OR RECOMMENDATIONS.`,
  },
  {
    id: "limitation",
    title: "Limitation of Liability",
    icon: Shield,
    content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ROOMVIA, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (B) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE; (C) ANY CONTENT OBTAINED FROM THE SERVICE; OR (D) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.

IN NO EVENT SHALL ROOMVIA'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT PAID BY YOU TO ROOMVIA DURING THE TWELVE (12) MONTH PERIOD PRIOR TO THE CLAIM.

SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR CERTAIN TYPES OF DAMAGES. ACCORDINGLY, SOME OF THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.`,
  },
  {
    id: "indemnification",
    title: "Indemnification",
    icon: Scale,
    content: `You agree to defend, indemnify, and hold harmless Roomvia, its officers, directors, employees, and agents, from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Service, your User Content, or your violation of these Terms.`,
  },
  {
    id: "termination",
    title: "Termination",
    icon: AlertTriangle,
    content: `We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including, without limitation, if you breach these Terms.

Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or delete your account through the account settings.

All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.`,
  },
  {
    id: "governing-law",
    title: "Governing Law and Dispute Resolution",
    icon: Globe,
    content: `These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.

Any dispute arising from or relating to these Terms or the Service shall first be attempted to be resolved through informal negotiation. If the dispute cannot be resolved through negotiation, it shall be submitted to binding arbitration in San Francisco, California, in accordance with the rules of the American Arbitration Association.

YOU AGREE THAT ANY CAUSE OF ACTION ARISING OUT OF OR RELATED TO THE SERVICE MUST COMMENCE WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES. OTHERWISE, SUCH CAUSE OF ACTION IS PERMANENTLY BARRED.`,
  },
  {
    id: "miscellaneous",
    title: "Miscellaneous",
    icon: MessageSquare,
    content: `These Terms constitute the entire agreement between you and Roomvia regarding the Service and supersede all prior agreements and understandings.

Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.

If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect.

We may assign our rights and obligations under these Terms, in whole or in part, to any party at any time without notice to you.

You may not assign your rights or obligations under these Terms without our prior written consent.`,
  },
]

export function TermsOfServicePage() {
  const lastUpdated = "December 9, 2024"
  // Define a constant for the header height to ensure consistent scrolling
  const HEADER_HEIGHT = 80 // in pixels, accounts for the navbar height plus some padding

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)

    // Add click event listeners to all anchor links for smooth scrolling with offset
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.currentTarget as HTMLAnchorElement
      const href = target.getAttribute("href")

      if (href && href.startsWith("#")) {
        e.preventDefault()
        const targetId = href.substring(1)
        const targetElement = document.getElementById(targetId)

        if (targetElement) {
          const yOffset = -HEADER_HEIGHT // Negative value to provide space above the element
          const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset

          window.scrollTo({
            top: y,
            behavior: "smooth",
          })
        }
      }
    }

    const anchorLinks = document.querySelectorAll('a[href^="#"]')
    anchorLinks.forEach((anchor) => {
      anchor.addEventListener("click", handleAnchorClick as EventListener)
    })

    // Clean up event listeners when component unmounts
    return () => {
      anchorLinks.forEach((anchor) => {
        anchor.removeEventListener("click", handleAnchorClick as EventListener)
      })
    }
  }, [])

  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-lg text-gray-600 mb-2">Please read these terms carefully before using our service.</p>
        <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
      </div>

      {/* Quick Navigation */}
      <Card className="mb-8 bg-[#40A1AB]/5">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-[#40A1AB] hover:text-[#3A919A] transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Sections */}
      <div className="space-y-8">
        {sections.map((section) => {
          const Icon = section.icon
          return (
            <Card key={section.id} id={section.id} className="scroll-mt-[80px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#40A1AB]/10 p-2 rounded-lg">
                    <Icon className="h-6 w-6 text-[#40A1AB]" />
                  </div>
                  <h2 className="text-2xl font-semibold">{section.title}</h2>
                </div>

                <div className="text-gray-700 leading-relaxed whitespace-pre-line">{section.content}</div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Contact Information */}
      <Card className="mt-8 scroll-mt-[80px]" id="contact">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>If you have any questions about these Terms of Service, please contact us:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">Roomvia Legal Team</p>
              <p>Email: support@roomvia.ai</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
